
import { EventBus } from "@/plugins/eventBus"
import Vue from "vue"
import { VueConstructor } from "vue/types/umd"
import * as pdfjsLib from "pdfjs-dist"
import { Document } from "./DocumentCategory.vue"
import SelectSpace from "./SelectSpace.vue"
import jsPDF from 'jspdf'
import axios from 'axios'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

export default (Vue as VueConstructor<Vue>).extend({
  name: 'AnonymizeDocumentModal',
  components: {
    SelectSpace
  },
  props: {
    originalCv: {   
      type: Object as () => Document,
      required: true,
    },
    spaceId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    // PDF objects
    pdfDoc: null as pdfjsLib.PDFDocumentProxy | null,
    pdfPage: null as pdfjsLib.PDFPageProxy | null,
    viewport: null as pdfjsLib.PageViewport | null,
    // State
    pdfLoaded: false,
    loadingPdf: false,
    activateManualAnonymization: false,
    isDrawingMode: false,
    isDrawing: false,
    // Drawing
    startX: 0,
    startY: 0,
    currentArea: null as Area | null,
    // Commentary
    currentCommentaryArea: null as (Area & { text: string }) | null,
    isCommentaryMode: false,
    // Watermark drawing
      currentWatermarkArea: null as Area | null,
      logoLoaded: false,
      logoImageElement: null as HTMLImageElement | null,
    // Watermark
    watermark: {
      enabled: false,
      x: 50,
      y: 50,
      width: 200,
      height: 100,
      isDragging: false,
      isResizing: false,
      dragStartX: 0,
      dragStartY: 0,
      resizeHandle: null as string | null,
      originalWidth: 200,
      originalHeight: 100,
      originalX: 50,
      originalY: 50
    },
    spaceData: null as {
      name: string,
      logo: string,
      address: string,
      city: string,
      zipcode: string,
      phone: string,
      default_notification_email: string
    } | null,
    watermarkImage: null as HTMLImageElement | null,
    // Canvas reference
    singleCanvas: null as HTMLCanvasElement | null,
    pdfImageData: null as ImageData | null, // PDF rendered image cache
    renderTask: null as any, // Current rendering task
    // Layout
    scale: 1.5,
    pageNum: 1,
    // PDF dimensions for conversion
    // pdfPageWidth: 0,
    // pdfPageHeight: 0,
    // Store original PDF path for cancel functionality
    originalPdfPath: null as string | null,
    // Store current display URL (for anonymized PDFs)
    currentDisplayUrl: null as string | null,
    // Store original CV for restoration - initialize with originalCv
    workingCv: null as Document | null,
    // File type detection
    fileType: 'pdf' as string,
    // Selected mode for button toggle
    selectedMode: null as string | null,
    // Commentary dialog
    commentaryDialog: false,
    commentaryText: '',
    // Watermark image cache to prevent flickering
    watermarkImageCache: new Map<string, HTMLImageElement>(),
    history: [] as Array<{
      x: number,
      y: number,
      width: number,
      height: number,
      background: string,
      'inline-content': string | null,
      deleted: boolean,
      pageNum: number,
      imageData?: string,
      spaceData?: {
        name: string,
        logo: string,
        address: string,
        city: string,
        zipcode: string,
        phone: string
      }
    }>,
    // Flag to prevent infinite loops during mode changes
    isChangingMode: false,
    // Debounce timer for mode changes
    modeChangeTimer: null as any,
    // Overlay interaction state
    overlayInteraction: {
      isActive: false,
      isDragging: false,
      isResizing: false,
      resizeHandle: null as string | null,
      dragStartX: 0,
      dragStartY: 0,
      originalX: 0,
      originalY: 0,
      originalWidth: 0,
      originalHeight: 0,
      targetEntry: null as any
    }
  }),
  computed: {
    canUndo() {
      return this.history.some(entry => !entry.deleted && entry.pageNum === this.pageNum);
    },
    canRedo() {
      return this.history.some(entry => entry.deleted && entry.pageNum === this.pageNum);
    },
    // Current drawing state
    isCurrentlyDrawing() {
      return !!(this.isDrawingMode && this.currentArea);
    },
    isCurrentlyCommenting() {
      return !!(this.isCommentaryMode && this.currentCommentaryArea);
    },
    // Canvas cursor state
    canvasCursor() {
      if (this.isDrawingMode || this.isCommentaryMode) {
        return 'crosshair';
      }
      if (this.selectedMode === 'watermark') {
        if (this.spaceData && this.watermarkImage) {
          return 'crosshair';
        } else {
          return 'not-allowed';
        }
      }
      return 'default';
    },
    logo() {
      if (this.spaceData && this.spaceData.logo) {
        if (['development', 'test'].includes(process.env.NODE_ENV)) {
          return require("@/assets/logos/" + this.spaceData.logo)
        } else {
          return '/img/logos/' + this.spaceData.logo
        }
      }
      return ''
    },
    // Get the last non-deleted entry from history for overlay display (current page only)
    lastNonDeletedEntry() {
      for (let i = this.history.length - 1; i >= 0; i--) {
        if (!this.history[i].deleted && this.history[i].pageNum === this.pageNum) {
          return this.history[i];
        }
      }
      return null;
    },
    // Get the current entry being drawn for overlay display
    currentOverlayEntry() {
      // If currently drawing a watermark, show the current watermark area
      if (this.selectedMode === 'watermark' && this.currentWatermarkArea && this.isDrawing) {
        return this.currentWatermarkArea;
      }
      // Otherwise show the last non-deleted history entry for current page
      for (let i = this.history.length - 1; i >= 0; i--) {
        if (!this.history[i].deleted && this.history[i].pageNum === this.pageNum) {
          return this.history[i];
        }
      }
      return null;
    }
  },
  // Add a watcher to detect when the modal is opened
  watch: {
    originalCv: {
      handler(newVal) {
        if (newVal && newVal.recto_path) {
          // Reset state when modal is opened with new CV
          this.workingCv = { ...newVal };
          this.currentDisplayUrl = null;
          this.currentArea = null;
          this.currentCommentaryArea = null;
          this.isDrawingMode = false;
          this.isCommentaryMode = false;
          this.activateManualAnonymization = false;
          this.selectedMode = null;
          this.history = [];
          this.isChangingMode = false;
          if (this.modeChangeTimer) {
            clearTimeout(this.modeChangeTimer);
            this.modeChangeTimer = null;
          }
          this.loadDocument();
        }
      },
      immediate: true
    },
  },
  async mounted() {
    this.singleCanvas = this.$refs.singleCanvas as HTMLCanvasElement;
    globalThis.addEventListener('resize', this.handleResize);
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    globalThis.removeEventListener('resize', this.handleResize);
    document.removeEventListener('keydown', this.handleKeyDown);
    // Clean up global mouse event listeners if they exist
    document.removeEventListener('mousemove', this.handleGlobalMouseMove);
    document.removeEventListener('mouseup', this.handleGlobalMouseUp);
  },
  methods: {

    // Detect file type from MIME type (content-type header only)
    async detectFileType(url: string): Promise<string> {
      if (!url) return 'unknown';
      
      try {
        const response = await fetch(url, { method: 'HEAD' });
        const contentType = response.headers.get('content-type');
        
        if (contentType) {
          if (contentType.startsWith('image/')) {
            return 'image';
          }
          if (contentType === 'application/pdf') {
            return 'pdf';
          }
        }
        
        return 'unknown';
      } catch (error) {
        console.error('Error detecting content-type:', error);
        return 'unknown';
      }
    },

    // Convert image to PDF using jsPDF
    async convertImageToPdf(imageUrl: string): Promise<string> {
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        // Handle CORS for different image sources
        if (imageUrl.startsWith('http') && !imageUrl.includes(globalThis.location.hostname)) {
          img.crossOrigin = 'anonymous';
        }
        
        // Timeout pour éviter les blocages
        const timeout = setTimeout(() => {
          reject(new Error('Image loading timeout'));
        }, 15000);
        
        img.onload = () => {
          clearTimeout(timeout);
          try {
            // Create a new jsPDF document
            const pdf = new jsPDF({
              orientation: img.width > img.height ? 'landscape' : 'portrait',
              unit: 'mm',
              format: 'a4'
            });

            // Calculate dimensions to fit the image in A4
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = img.width;
            const imgHeight = img.height;
            
            // Calculate scaling to fit image in page
            const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;
            
            // Center the image on the page
            const x = (pageWidth - scaledWidth) / 2;
            const y = (pageHeight - scaledHeight) / 2;
            
            // Add image to PDF with correct format
            const imageFormat = this.getImageFormat(imageUrl);
            pdf.addImage(img, imageFormat.toUpperCase(), x, y, scaledWidth, scaledHeight);
            
            // Convert to blob URL
            const pdfBlob = pdf.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);
            
            resolve(pdfUrl);
          } catch (error) {
            console.error('Error creating PDF from image:', error);
            reject(error);
          }
        };
        
        img.onerror = (error) => {
          clearTimeout(timeout);
          console.error('Image loading error:', error);
          reject(new Error(`Failed to load image: ${imageUrl}`));
        };
        
        img.src = imageUrl;
      });
    },

    // Helper method to get image format from URL
    getImageFormat(url: string): string {
      const extension = url.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'jpg':
        case 'jpeg':
          return 'jpeg';
        case 'png':
          return 'png';
        case 'gif':
          return 'gif';
        case 'bmp':
          return 'bmp';
        case 'webp':
          return 'webp';
        default:
          return 'png'; // Default fallback
      }
    },

    async loadDocument() {
      const cvToUse = this.workingCv || this.originalCv;
      if (!cvToUse?.recto_path) return;
      
      this.loadingPdf = true;
      this.pdfLoaded = false;
      
      // Reset page number to 1 when loading a new document
      this.pageNum = 1;
      
      try {
        const displayUrl = this.currentDisplayUrl || cvToUse.recto_path;
        
        // Use MIME type as the primary source of truth
        this.fileType = await this.detectFileType(displayUrl);
        
        let documentUrl = displayUrl;
        
        // Convert image to PDF if needed
        if (this.fileType === 'image') {
          try {
            documentUrl = await this.convertImageToPdf(displayUrl);
            
            // Test if the converted PDF can be loaded
            const testResponse = await fetch(documentUrl);
            if (!testResponse.ok) {
              throw new Error('Converted PDF is not accessible');
            }
            
          } catch (error) {
            console.error('Image conversion failed:', error);
            EventBus.$emit('snackbar', { 
              message: 'Erreur lors de la conversion de l\'image en PDF', 
              color: 'error'
            });
            return;
          }
        }

        // Cancel any ongoing rendering operation
        if (this.renderTask) {
          this.renderTask.cancel();
          this.renderTask = null;
        }

        // Load with PDF.js only
        const loadingTask = pdfjsLib.getDocument(documentUrl);
        this.pdfDoc = await loadingTask.promise;
        
        await this.renderPdf();
        this.pdfLoaded = true;
      } catch (error) {
        if (error instanceof Error && error.name === 'RenderingCancelledException') {
          return;
        }
        console.error('Document loading error:', error);
        EventBus.$emit('snackbar', { 
          message: this.fileType === 'image' 
            ? (this.$vuetify.lang.t('$vuetify.error_loading_image') || 'Erreur lors du chargement de l\'image')
            : (this.$vuetify.lang.t('$vuetify.error_loading_pdf') || 'Erreur lors du chargement du PDF'), 
          color: 'error'
        });
      } finally {
        this.loadingPdf = false;
      }
    },

    async renderPdf() {
      if (!this.pdfDoc || !this.singleCanvas) return;
      try {
        // Cancel any ongoing rendering operation
        if (this.renderTask) {
          this.renderTask.cancel();
          this.renderTask = null;
        }
        
        // Ensure pageNum is within valid range
        if (this.pageNum > this.pdfDoc.numPages) {
          this.pageNum = 1;
        }
        
        // Get the page
        this.pdfPage = await this.pdfDoc.getPage(this.pageNum);
        // Create viewport with fixed scale
        this.viewport = this.pdfPage.getViewport({ scale: this.scale });
        // Set canvas dimensions
        this.singleCanvas.width = this.viewport?.width;
        this.singleCanvas.height = this.viewport?.height;
        // Set CSS dimensions
        this.singleCanvas.style.width = this.viewport?.width + 'px';
        this.singleCanvas.style.height = this.viewport?.height + 'px';
        
        // Render PDF to canvas
        const context = this.singleCanvas.getContext('2d');
        if (!context) return;
        const renderContext = {
          canvasContext: context,
          viewport: this.viewport
        };
        
        // Store rendering task
        this.renderTask = this.pdfPage.render(renderContext);
        await this.renderTask.promise;
        
        // Clean up rendering task
        this.renderTask = null;
        
        // Save PDF rendered image to avoid re-rendering
        this.pdfImageData = context.getImageData(0, 0, this.singleCanvas.width, this.singleCanvas.height);
        
        // Apply all changes from history
        await this.applyHistoryToCanvas(context);
        
      } catch (error) {
        // Ignore rendering cancelled error which is normal
        if (error instanceof Error && error.name === 'RenderingCancelledException') {
          return;
        }
        throw error; // Re-throw other errors
      }
    },



    async applyHistoryToCanvas(context: CanvasRenderingContext2D) {
      if (!context) return;
      
      // Process all non-deleted history entries
      const watermarkPromises: Promise<void>[] = [];
      
      this.history.forEach((entry) => {
        if (!entry.deleted && entry.pageNum === this.pageNum) {
          if (entry.background === 'watermark' && entry.imageData) {
            // Draw watermark image from stored image data
            
            // Use cached image or create new one
            const watermarkPromise = new Promise<void>((resolve) => {
              const imageData = entry.imageData!;
              let img = this.watermarkImageCache.get(imageData);
              
              if (img && img.complete) {
                // Use cached image
                context.drawImage(img, entry.x, entry.y, entry.width, entry.height);
                resolve();
              } else {
                // Create new image and cache it
                img = new Image();
                img.onload = () => {
                  this.watermarkImageCache.set(imageData, img!);
                  context.drawImage(img!, entry.x, entry.y, entry.width, entry.height);
                  resolve();
                };
                img.onerror = () => {
                  console.error('Failed to load watermark image in preview:', entry.imageData);
                  resolve(); // Continue even if image fails to load
                };
                img.src = imageData;
              }
            });
            watermarkPromises.push(watermarkPromise);
          } else {
            // Draw regular zones (white zones, commentary)
            context.fillStyle = entry.background;
            context.fillRect(entry.x, entry.y, entry.width, entry.height);
            
            // Draw inline content if available
            if (entry['inline-content']) {
              this.drawWrappedText(context, entry['inline-content'], entry.x, entry.y, entry.width, entry.height);
            }
          }
        }
      });
      
      // Wait for all watermark images to load before continuing
      await Promise.all(watermarkPromises);
      
      // Draw current area being drawn (for real-time feedback)
      if (this.isCurrentlyDrawing && this.currentArea) {
        context.fillStyle = 'white';
        context.fillRect(this.currentArea.x, this.currentArea.y, this.currentArea.width, this.currentArea.height);
      }
      
      // Draw current commentary area being drawn
      if (this.isCurrentlyCommenting && this.currentCommentaryArea) {
        context.fillStyle = '#F6F8E0';
        context.fillRect(this.currentCommentaryArea.x, this.currentCommentaryArea.y, this.currentCommentaryArea.width, this.currentCommentaryArea.height);
      }
      
      // Draw current watermark area being drawn
      if (this.selectedMode === 'watermark' && this.currentWatermarkArea && this.watermarkImage) {
        context.drawImage(this.watermarkImage, this.currentWatermarkArea.x, this.currentWatermarkArea.y, this.currentWatermarkArea.width, this.currentWatermarkArea.height);
      }
    },

    saveToHistory(entryToAdd: {
      x: number,
      y: number,
      width: number,
      height: number,
      background: string,
      'inline-content': string | null,
      deleted: boolean,
      pageNum: number,
      imageData?: string
    }) {
      // Prevent saving if we're in the middle of a mode change to avoid infinite loops
      if (this.isChangingMode) return;
      
      // Truncate deleted operations after the last non-deleted operation
      let lastNonDeletedIndex = -1;
      for (let i = this.history.length - 1; i >= 0; i--) {
        if (!this.history[i].deleted) {
          lastNonDeletedIndex = i;
          break;
        }
      }
      if (lastNonDeletedIndex >= 0) {
        this.history = this.history.slice(0, lastNonDeletedIndex + 1);
      }

      // Add the new operation to history
      this.history.push(entryToAdd);
    },

    updateLastHistoryEntry(area: { x: number, y: number, width: number, height: number }, background: string) {
      if (this.history.length > 0) {
        const lastEntry = this.history[this.history.length - 1];
        if (lastEntry.background === background && !lastEntry.deleted) {
          lastEntry.x = area.x;
          lastEntry.y = area.y;
          lastEntry.width = area.width;
          lastEntry.height = area.height;
        }
      }
    },

    removeLastHistoryEntry(background: string) {
      if (this.history.length > 0) {
        const lastEntry = this.history[this.history.length - 1];
        if (lastEntry.background === background && !lastEntry.deleted) {
          this.history.pop();
        }
      }
    },

    undo() {
      if (!this.canUndo) return;
      
      // Set flag to prevent history saves during undo
      this.isChangingMode = true;
      
      // Find the last non-deleted entry for current page and mark it as deleted
      for (let i = this.history.length - 1; i >= 0; i--) {
        if (!this.history[i].deleted && this.history[i].pageNum === this.pageNum) {
          this.history[i].deleted = true;
          break;
        }
      }
      
      // Restore state from current history (non-deleted entries)
      this.restoreState();
      
      // Reset flag after undo is complete
      this.$nextTick(() => {
        this.isChangingMode = false;
      });
    },

    redo() {
      if (!this.canRedo) return;
      
      // Set flag to prevent history saves during redo
      this.isChangingMode = true;
      
      // Find the first deleted entry for current page and mark it as not deleted
      const firstDeletedIndex = this.history.findIndex(entry => entry.deleted && entry.pageNum === this.pageNum);
      if (firstDeletedIndex !== -1) {
        this.history[firstDeletedIndex].deleted = false;
      }
      
      // Restore state from current history (non-deleted entries)
      this.restoreState();
      
      // Reset flag after redo is complete
      this.$nextTick(() => {
        this.isChangingMode = false;
      });
    },

    async restoreState() {
      // Simply redraw canvas with current history state
      await this.redrawCanvas();
    },

    async onModeChange(mode: string | null) {
      // Clear any existing timer
      if (this.modeChangeTimer) {
        clearTimeout(this.modeChangeTimer);
      }
      
      // Debounce mode changes to prevent rapid switching
      this.modeChangeTimer = setTimeout(async () => {
        // Set flag to prevent infinite loops
        this.isChangingMode = true;
        
        
        if (mode === 'drawing') {
          await this.enableDrawingMode();
        } else if (mode === 'watermark') {
          await this.enableWatermarkMode();
        } else if (mode === 'commentary') {
          await this.enableCommentaryMode();
        } else {
          // No mode selected - disable all features
          this.resetAllModes();
          await this.redrawZonesOnly();
        }
        
        // Reset flag after mode change is complete
        this.$nextTick(() => {
          this.isChangingMode = false;
        });
      }, 50); // 50ms debounce
    },

    resetAllModes() {
      this.isDrawingMode = false;
        this.isDrawing = false;
        this.currentArea = null;
      this.isCommentaryMode = false;
      this.currentCommentaryArea = null;
      this.currentWatermarkArea = null;
      this.watermark.enabled = false;
      this.activateManualAnonymization = false;
    },

    async enableDrawingMode() {
      this.resetAllModes();
      this.isDrawingMode = true;
      this.activateManualAnonymization = true;
      await this.redrawZonesOnly();
    },

    async enableWatermarkMode() {
      this.resetAllModes();
      this.watermark.enabled = true;
      this.activateManualAnonymization = true;
      await this.redrawCanvas();
    },

    async enableCommentaryMode() {
      this.resetAllModes();
      this.isCommentaryMode = true;
      this.activateManualAnonymization = true;
      await this.redrawCanvas();
    },

    async toggleDrawingMode() {
      // This method is kept for compatibility but now works with the toggle
      if (this.selectedMode !== 'drawing') {
        this.selectedMode = 'drawing';
        this.enableDrawingMode();
      } else {
        this.selectedMode = null;
        // Directly disable all features without calling onModeChange to avoid recursion
        this.resetAllModes();
        await this.redrawZonesOnly();
      }
    },

    handleCanvasMouseDown(event: MouseEvent) {
      if (this.selectedMode === 'watermark') {
        if (this.spaceData) {
          // Check if clicking on existing watermark for interaction
          if (this.watermarkImage && this.isClickingOnExistingWatermark(event)) {
            this.startWatermarkInteraction(event);
          } else {
            this.startWatermarkDrawing(event);
          }
        }
      } else {
        this.startDrawing(event);
      }
    },

    isClickingOnExistingWatermark(event: MouseEvent): boolean {
      if (!this.singleCanvas) return false;
      
      const rect = this.singleCanvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      // Check if click is within any existing watermark in history
      return this.history.some(entry => 
        entry.background === 'watermark' && 
        !entry.deleted &&
        mouseX >= entry.x && mouseX <= entry.x + entry.width &&
        mouseY >= entry.y && mouseY <= entry.y + entry.height
      );
    },

    handleCanvasMouseMove(event: MouseEvent) {
      if (this.selectedMode === 'watermark') {
        if (this.spaceData && this.watermarkImage) {
          if (this.watermark.isDragging || this.watermark.isResizing) {
            this.handleWatermarkInteraction(event);
          } else if (this.isDrawing) {
            this.drawWatermark(event);
          }
        }
        // Update cursor based on mouse position over watermark
        this.updateWatermarkCursor(event);
      } else {
        this.draw(event);
      }
    },

    updateWatermarkCursor(event: MouseEvent) {
      if (!this.singleCanvas || !this.watermarkImage) return;
      
      const rect = this.singleCanvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      // Check if mouse is over watermark
      if (mouseX >= this.watermark.x && mouseX <= this.watermark.x + this.watermark.width &&
          mouseY >= this.watermark.y && mouseY <= this.watermark.y + this.watermark.height) {
        
        // Check if mouse is on a resize handle
        const handleSize = 8;
        const resizeHandle = this.isOnResizeHandle(mouseX, mouseY, handleSize);
        
        if (resizeHandle) {
          // Set appropriate resize cursor
          const cursors = {
            'nw': 'nw-resize',
            'ne': 'ne-resize',
            'sw': 'sw-resize',
            'se': 'se-resize'
          };
          this.singleCanvas.style.cursor = cursors[resizeHandle as keyof typeof cursors];
        } else {
          // Set move cursor for dragging
          this.singleCanvas.style.cursor = 'move';
        }
      } else {
        // Reset to default cursor
        this.singleCanvas.style.cursor = 'crosshair';
      }
    },

    handleCanvasMouseUp(event: MouseEvent) {
      if (this.selectedMode === 'watermark') {
        if (this.spaceData && this.watermarkImage) {
          if (this.watermark.isDragging || this.watermark.isResizing) {
            this.stopWatermarkInteraction(event);
          } else if (this.isDrawing) {
            this.stopWatermarkDrawing(event);
          }
        }
      } else {
        this.stopDrawing(event);
      }
    },

    handleCanvasMouseLeave(event: MouseEvent) {
      this.stopDrawing(event);
    },

    startDrawing(event: MouseEvent) {
      if ((!this.isDrawingMode && !this.isCommentaryMode) || !this.singleCanvas) return;
      event.preventDefault();
      this.isDrawing = true;
      const rect = this.singleCanvas.getBoundingClientRect();
      this.startX = event.clientX - rect.left;
      this.startY = event.clientY - rect.top;
      
      // Create initial history entry for white zones
      if (this.isDrawingMode) {
        this.saveToHistory({
          x: this.startX,
          y: this.startY,
          width: 0,
          height: 0,
          background: 'white',
          'inline-content': null,
          deleted: false,
          pageNum: this.pageNum
        });
      }
      
      // Create initial history entry for commentary zones
      if (this.isCommentaryMode) {
        this.saveToHistory({
          x: this.startX,
          y: this.startY,
          width: 0,
          height: 0,
          background: '#F6F8E0',
          'inline-content': '',
          deleted: false,
          pageNum: this.pageNum
        });
      }
    },

    async draw(event: MouseEvent) {
      if (!this.isDrawing || !this.singleCanvas) return;
      event.preventDefault();
      const rect = this.singleCanvas.getBoundingClientRect();
      const currentX = event.clientX - rect.left;
      const currentY = event.clientY - rect.top;
      
      const area = {
        x: Math.min(this.startX, currentX),
        y: Math.min(this.startY, currentY),
        width: Math.abs(currentX - this.startX),
        height: Math.abs(currentY - this.startY)
      };
      
      if (this.isDrawingMode) {
        this.currentArea = area;
        this.updateLastHistoryEntry(area, 'white');
      } else if (this.isCommentaryMode) {
        this.currentCommentaryArea = { ...area, text: '' };
        this.updateLastHistoryEntry(area, '#F6F8E0');
      }
      
      // Use cached image data for smooth drawing without flickering
      await this.redrawZonesOnly();
    },

    async stopDrawing(event: MouseEvent) {
      if (!this.isDrawing) return;
      event.preventDefault();
      this.isDrawing = false;
      
      if (this.isDrawingMode && this.currentArea) {
        if (this.currentArea.width > 10 && this.currentArea.height > 10) {
          // Finalize the white zone - the entry already exists and was being updated
      this.currentArea = null;
        } else {
          // Remove the entry if it's too small
          this.removeLastHistoryEntry('white');
          this.currentArea = null;
        }
      } else if (this.isCommentaryMode && this.currentCommentaryArea) {
        if (this.currentCommentaryArea.width > 10 && this.currentCommentaryArea.height > 10) {
          // Open dialog for commentary text input
          this.commentaryText = '';
          this.commentaryDialog = true;
        } else {
          // Remove the entry if it's too small
          this.removeLastHistoryEntry('#F6F8E0');
          this.currentCommentaryArea = null;
        }
      }
      
      // Use cached image data instead of full re-render to prevent flickering
      await this.redrawZonesOnly();
    },

    async confirmCommentary() {
      if (this.commentaryText.trim()) {
        // Update the existing commentary entry with the text
        if (this.history.length > 0) {
          const lastEntry = this.history[this.history.length - 1];
          if (lastEntry.background === '#F6F8E0' && !lastEntry.deleted) {
            lastEntry['inline-content'] = this.commentaryText.trim();
          }
        }
        this.currentCommentaryArea = null;
        
        // Redraw canvas to show the updated commentary text
        await this.redrawZonesOnly();
      }
      this.commentaryDialog = false;
      this.commentaryText = '';
    },

    async cancelCommentary() {
      // User cancelled - remove the entry
      this.removeLastHistoryEntry('#F6F8E0');
      this.currentCommentaryArea = null;
      
      // Redraw canvas to remove the commentary zone
      await this.redrawZonesOnly();
      
      this.commentaryDialog = false;
      this.commentaryText = '';
    },

    async redrawCanvas() {
      if (!this.singleCanvas || !this.pdfPage || !this.viewport) return;
      // Use unified renderPdf method
      this.renderPdf();
    },

    async redrawZonesOnly() {
      if (!this.singleCanvas || !this.pdfImageData) return;
      const context = this.singleCanvas.getContext('2d');
      if (!context) return;
      
      // Restore PDF image from cache (no flickering)
      context.putImageData(this.pdfImageData, 0, 0);
      
      // Apply current state to canvas
      await this.applyHistoryToCanvas(context);
    },
    async savePdfDocument() {
      if (!this.pdfDoc || !this.singleCanvas) return;
      try {
        // For single page, use the original method
        if (this.pdfDoc.numPages === 1) {
          await this.saveSinglePagePdf();
          return;
        }

        // For multi-page, create a new PDF document
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        // Process each page
        for (let pageNum = 1; pageNum <= this.pdfDoc.numPages; pageNum++) {
          // Get the page
          const page = await this.pdfDoc.getPage(pageNum);
          const viewport = page.getViewport({ scale: this.scale });
          
          // Create a temporary canvas for this page
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
          if (!context) continue;
        
        // Set canvas size to match viewport
          canvas.width = viewport.width;
          canvas.height = viewport.height;
        
          // Render PDF page to canvas
        const renderContext = {
          canvasContext: context,
            viewport: viewport
        };
        
          await page.render(renderContext).promise;
        
          // Draw all non-deleted operations from history for this page
        const watermarkPromises: Promise<void>[] = [];
        
        this.history.forEach((entry) => {
            if (!entry.deleted && entry.pageNum === pageNum) {
            if (entry.background === 'watermark' && entry.imageData) {
              // Draw watermark image from stored image data
              const watermarkPromise = new Promise<void>((resolve) => {
                const imageData = entry.imageData!;
                let img = this.watermarkImageCache.get(imageData);
                
                if (img && img.complete) {
                  // Use cached image
                  context.drawImage(img, entry.x, entry.y, entry.width, entry.height);
                  resolve();
                } else {
                  // Create new image and cache it
                  img = new Image();
                  img.onload = () => {
                    this.watermarkImageCache.set(imageData, img!);
                    context.drawImage(img!, entry.x, entry.y, entry.width, entry.height);
                    resolve();
                  };
                  img.onerror = () => {
                    console.error('Failed to load watermark image:', entry.imageData);
                    resolve(); // Continue even if image fails to load
                  };
                  img.src = imageData;
                }
              });
              watermarkPromises.push(watermarkPromise);
            } else {
              // Set background color
              context.fillStyle = entry.background;
              context.fillRect(entry.x, entry.y, entry.width, entry.height);
              
              // Draw inline content if available
              if (entry['inline-content']) {
                  this.drawWrappedText(context, entry['inline-content'], entry.x, entry.y, entry.width, entry.height);
              }
            }
          }
        });
        
        // Wait for all watermark images to load before continuing
        await Promise.all(watermarkPromises);
        
          // Convert canvas to image with better quality
          const imageData = canvas.toDataURL('image/jpeg', 0.95);
          
          // Add page to PDF (except for first page which is already created)
          if (pageNum > 1) {
            pdf.addPage();
          }
          
          // Calculate dimensions to fit the image in A4
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          
          // Calculate scaling to fit image in page
          const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
          const scaledWidth = imgWidth * ratio;
          const scaledHeight = imgHeight * ratio;
          
          // Center the image on the page
          const x = (pageWidth - scaledWidth) / 2;
          const y = (pageHeight - scaledHeight) / 2;
          
          // Add image to PDF with JPEG format
          pdf.addImage(imageData, 'JPEG', x, y, scaledWidth, scaledHeight);
        }
        
        // Convert to blob URL for display
        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Store the anonymized URL for display
        this.currentDisplayUrl = pdfUrl;
        
        // Reload PDF with new anonymized file
        await this.loadDocument();
        
        // Reset current area and disable drawing mode
        this.currentArea = null;
        this.isDrawingMode = false;
        this.activateManualAnonymization = false;
        
        // Convert to bytes for saving
        const pdfBytes = await pdfBlob.arrayBuffer();
        this.$emit('saveDocument', pdfBytes);
        
      } catch(error) {
        console.error('Error saving PDF document:', error)
        EventBus.$emit('snackbar', { 
          message: this.$vuetify.lang.t('$vuetify.error_saving_pdf'), 
          color: 'error'
        });
      }
    },

    async saveSinglePagePdf() {
      if (!this.pdfPage || !this.viewport || !this.singleCanvas) return;
      
      // Create a temporary canvas for image conversion
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;
      
      // Set canvas size to match viewport
      canvas.width = this.viewport.width;
      canvas.height = this.viewport.height;
      
      // Render PDF to canvas
      const renderContext = {
        canvasContext: context,
        viewport: this.viewport
      };
      
      await this.pdfPage.render(renderContext).promise;
      
      // Draw all non-deleted operations from history
      const watermarkPromises: Promise<void>[] = [];
      
      this.history.forEach((entry) => {
        if (!entry.deleted && entry.pageNum === this.pageNum) {
          if (entry.background === 'watermark' && entry.imageData) {
            // Draw watermark image from stored image data
            const watermarkPromise = new Promise<void>((resolve) => {
              const imageData = entry.imageData!;
              let img = this.watermarkImageCache.get(imageData);
              
              if (img && img.complete) {
                // Use cached image
                context.drawImage(img, entry.x, entry.y, entry.width, entry.height);
                resolve();
              } else {
                // Create new image and cache it
                img = new Image();
                img.onload = () => {
                  this.watermarkImageCache.set(imageData, img!);
                  context.drawImage(img!, entry.x, entry.y, entry.width, entry.height);
                  resolve();
                };
                img.onerror = () => {
                  console.error('Failed to load watermark image:', entry.imageData);
                  resolve(); // Continue even if image fails to load
                };
                img.src = imageData;
              }
            });
            watermarkPromises.push(watermarkPromise);
          } else {
            // Set background color
            context.fillStyle = entry.background;
            context.fillRect(entry.x, entry.y, entry.width, entry.height);
            
            // Draw inline content if available
            if (entry['inline-content']) {
              this.drawWrappedText(context, entry['inline-content'], entry.x, entry.y, entry.width, entry.height);
            }
          }
        }
      });
      
      // Wait for all watermark images to load before continuing
      await Promise.all(watermarkPromises);
      
      // Convert canvas to image
      const imageData = canvas.toDataURL('image/jpeg', 0.95);
      
      // Create a new PDF document using jsPDF (same as multi-page)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate dimensions to fit the image in A4
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate scaling to fit image in page
      const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      
      // Center the image on the page
      const x = (pageWidth - scaledWidth) / 2;
      const y = (pageHeight - scaledHeight) / 2;
      
      // Add image to PDF
      pdf.addImage(imageData, 'JPEG', x, y, scaledWidth, scaledHeight);
      
      // Convert to blob URL for display
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Store the anonymized URL for display
      this.currentDisplayUrl = pdfUrl;
      
      // Reload PDF with new anonymized file
      await this.loadDocument();
      
      // Reset current area and disable drawing mode
      this.currentArea = null;
      this.isDrawingMode = false;
      this.activateManualAnonymization = false;
      
      // Convert to bytes for saving
      const pdfBytes = await pdfBlob.arrayBuffer();
      this.$emit('saveDocument', pdfBytes);
    },


    handleResize() {
      if (this.pdfLoaded) {
        this.$nextTick(() => {
          this.renderPdf();
        });
      }
    },

    async previousPage() {
      if (this.pageNum > 1) {
        this.pageNum--;
        await this.renderPdf();
      }
    },

    async nextPage() {
      if (this.pdfDoc && this.pageNum < this.pdfDoc.numPages) {
        this.pageNum++;
        await this.renderPdf();
      }
    },

    handleKeyDown(event: KeyboardEvent) {
      // Only handle shortcuts when modal is active and PDF is loaded
      if (!this.pdfLoaded) return;
      
      // Check if Ctrl key is pressed (or Cmd on Mac)
      const isCtrlPressed = event.ctrlKey || event.metaKey;
      
      if (!isCtrlPressed) return;
      
      // Prevent default browser behavior for these shortcuts
      event.preventDefault();
      
      switch (event.key.toLowerCase()) {
        case 'z':
          // Ctrl+Z: Undo
          if (this.canUndo) {
            this.undo();
          }
          break;
        case 'y':
          // Ctrl+Y: Redo
          if (this.canRedo) {
            this.redo();
          }
          break;
        case 's':
          // Ctrl+S: Save
          if (this.history.length > 0) {
            this.savePdfDocument();
          }
          break;
      }
    },

    cancelAnonymization() {
      // Set flag to prevent history saves during cancel
      this.isChangingMode = true;
      
      // Clear current drawing state
      this.currentArea = null;
      this.currentCommentaryArea = null;
      
      // Clear watermark image cache
      this.watermarkImageCache.clear();
      
      // Reset to original PDF
      this.currentDisplayUrl = null;
      this.loadDocument();
      this.workingCv = this.originalCv;
      // Reset drawing state
      this.currentArea = null;
      this.currentCommentaryArea = null;
      this.isDrawingMode = false;
      this.isCommentaryMode = false;
      this.activateManualAnonymization = false;
      // Reset toggle to no mode selected
      this.selectedMode = null;
      // Reset history
      this.history = [];
      
      // Reset flag after cancel is complete
      this.$nextTick(() => {
        this.isChangingMode = false;
      });
    },

    closeModal() {
      if (this.renderTask) {
        this.renderTask.cancel();
        this.renderTask = null;
      }
      
      this.pdfLoaded = false;
      this.pdfDoc = null;
      this.isDrawingMode = false;
      this.isDrawing = false;
      this.currentArea = null;
      this.isCommentaryMode = false;
      this.currentCommentaryArea = null;
      this.activateManualAnonymization = false;
      this.pdfImageData = null;
      
      // Clear watermark image cache
      this.watermarkImageCache.clear();
      this.originalPdfPath = null;
      this.selectedMode = null;
      this.history = [];
      this.isChangingMode = false;
      if (this.modeChangeTimer) {
        clearTimeout(this.modeChangeTimer);
        this.modeChangeTimer = null;
      }
      this.spaceData = null;
      this.watermarkImageCache.clear();
      this.$emit('closeModal');
    },

    // Watermark methods
    async onSpaceSelected(spaceId: any) {
      // Reset space data first
      this.spaceData = null;
      this.watermarkImage = null;
      
      if (!spaceId) {
        return;
      }
      
      try {
        // Make API call to get full space data
        const response = await axios.get(`/v1/spaces/${spaceId}`);
        
        if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
          return;
        }
        
        const spaceData = response.data[0];
        
        if (!spaceData) {
          return;
        }
        
        this.spaceData = {
          name: spaceData.name,
          logo: spaceData.logo,
          address: spaceData.address,
          city: spaceData.city,
          zipcode: spaceData.zipcode,
          phone: spaceData.phone,
          default_notification_email: spaceData.default_notification_email
        };
        
        // Reset logo loading state
        this.logoLoaded = false;
        this.logoImageElement = null;
        
        // Create watermark preview immediately if no logo
        if (!spaceData.logo || !spaceData.logo.trim()) {
          this.createWatermarkPreview();
        }
        // Otherwise, the watermark preview will be created when the logo loads via the hidden v-img
        
      } catch (error) {
        console.error('Error fetching space data:', error);
        // Don't show snackbar, just silently fail
        this.spaceData = null;
        this.watermarkImage = null;
      }
    },


    onLogoLoaded() {
      this.logoLoaded = true;
      
      // retrieve logo uri
      const logoUri = (this.$refs.logoPreview as Vue & { currentSrc: string }).currentSrc;
      
      if (logoUri) {
        // Create a new Image object to get dimensions and use for drawing
        const img = new Image();
        img.onload = () => {
          this.logoImageElement = img;
          
          // Now create the watermark preview with the loaded logo
          this.createWatermarkPreview();
        };
        img.onerror = (error) => {
          this.createWatermarkPreview(); // Create without logo
        };
        img.src = logoUri;
      } else {
        this.createWatermarkPreview(); // Create without logo
      }
    },

    onLogoError(error: any) {
      this.logoLoaded = false;
      this.logoImageElement = null;
      
      // Create watermark preview without logo
      this.createWatermarkPreview();
    },

    calculateWatermarkDimensions() {
      if (!this.spaceData) {
        // Default dimensions if no space data
        this.watermark.width = 120;
        this.watermark.height = 30;
        return;
      }

      // Create a temporary canvas to measure text
      const tempCanvas = document.createElement('canvas');
      const tempContext = tempCanvas.getContext('2d');
      if (!tempContext) {
        this.watermark.width = 120;
        this.watermark.height = 30;
        return;
      }

      // Set font for text measurement
      tempContext.font = 'bold 12px Arial';
      const lineHeight = 14;
      const padding = 16; // 8px on each side

      // Calculate logo dimensions
      let logoWidth = 0;
      let logoHeight = 0;
      if (this.spaceData.logo && this.spaceData.logo.trim() && this.logoLoaded && this.logoImageElement) {
        const maxLogoHeight = 100;
        const logoAspectRatio = this.logoImageElement.naturalWidth / this.logoImageElement.naturalHeight;
        logoHeight = Math.min(maxLogoHeight, 100);
        logoWidth = logoHeight * logoAspectRatio;
      }

      // Calculate text dimensions
      const lines = this.getSpaceDataLines();

      // Find the longest text line width
      let maxTextWidth = 0;
      lines.forEach(line => {
        if (line && line.trim()) {
          const textWidth = tempContext.measureText(line).width;
          maxTextWidth = Math.max(maxTextWidth, textWidth);
        }
      });

      // Calculate required dimensions
      const textHeight = lines.filter(line => line && line.trim()).length * lineHeight;
      const requiredWidth = Math.max(logoWidth, maxTextWidth) + padding;
      
      // Calculate height based on whether there's a logo or not
      let requiredHeight;
      if (logoHeight > 0) {
        // With logo: logo + padding + text + bottom padding
        requiredHeight = logoHeight + 8 + textHeight + padding;
      } else {
        // Without logo: just text + padding (top and bottom)
        requiredHeight = textHeight + padding;
      }

      // Set dimensions with some minimum values, but allow smaller sizes for minimal content
      this.watermark.width = Math.max(120, requiredWidth);
      this.watermark.height = Math.max(30, requiredHeight);
    },

    createWatermarkPreview() {
      if (!this.spaceData) {
        return;
      }

      // Calculate real dimensions based on content
      this.calculateWatermarkDimensions();
      
      // Create a temporary canvas to generate the watermark preview image
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      
      // Set canvas size with higher resolution for better image quality
      const scale = 2; // 2x resolution for crisp rendering
      canvas.width = this.watermark.width * scale;
      canvas.height = this.watermark.height * scale;
      
      // Scale the context to match the device pixel ratio
      context.scale(scale, scale);
      
      // Draw white background
      context.fillStyle = 'white';
      context.fillRect(0, 0, this.watermark.width, this.watermark.height);
      
      // Draw logo if available and loaded
      if (this.spaceData.logo && this.spaceData.logo.trim() && this.logoLoaded && this.logoImageElement) {
        // Draw logo first with maintained aspect ratio (left-aligned at top)
        // Limit logo by height (max 100px) while preserving aspect ratio
        const maxLogoHeight = 100;
        const logoAspectRatio = this.logoImageElement.naturalWidth / this.logoImageElement.naturalHeight;
        
        let logoWidth, logoHeight;
        // Always limit by height first
        logoHeight = Math.min(maxLogoHeight, 100); // Max 100px
        logoWidth = logoHeight * logoAspectRatio;
        
        const logoX = 8; // Left-aligned
        const logoY = 8; // Top of canvas
        
        context.drawImage(this.logoImageElement, logoX, logoY, logoWidth, logoHeight);
        
        // Then draw text content below the logo (left-aligned)
        context.fillStyle = '#333';
        context.font = 'bold 12px Arial';
        context.textAlign = 'left'; // Left-align text
        context.textBaseline = 'top';
        
        const textX = 8; // Left-aligned (same as logo)
        const textY = logoY + logoHeight + 8; // Below logo with padding
        const lineHeight = 14;
        
        const lines = this.getSpaceDataLines();
        
        
        lines.forEach((line, index) => {
          if (line && line.trim()) {
            context.fillText(line, textX, textY + (index * lineHeight));
          }
        });
        
        // Create preview image (not added to history)
        this.watermarkImage = new Image();
        this.watermarkImage.onload = () => {
          // Image loaded successfully
        };
        this.watermarkImage.src = canvas.toDataURL();
      } else {
        this.createWatermarkPreviewWithoutLogo(canvas);
      }
    },

    createWatermarkPreviewWithoutLogo(canvas: HTMLCanvasElement) {
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      
      // Calculate real dimensions based on content
      this.calculateWatermarkDimensions();
      
      // Calculate required dimensions first
      const lineHeight = 14;
      const lines = this.getSpaceDataLines();
      
      
      // Set high resolution for better text quality
      const scale = 2;
      canvas.width = this.watermark.width * scale;
      canvas.height = this.watermark.height * scale;
      context.scale(scale, scale);
      
      // Draw white background
      context.fillStyle = 'white';
      context.fillRect(0, 0, this.watermark.width, this.watermark.height);
      
      // Draw text content (left-aligned)
      context.fillStyle = '#333';
      context.font = 'bold 12px Arial';
      context.textAlign = 'left';
      context.textBaseline = 'top';
      
      const textX = 8; // Left-aligned
      const textY = 8;
      
      
      lines.forEach((line, index) => {
        if (line && line.trim()) {
          context.fillText(line, textX, textY + (index * lineHeight));
        }
      });
      
      this.watermarkImage = new Image();
      this.watermarkImage.onload = () => {
        // Image loaded successfully
      };
      this.watermarkImage.src = canvas.toDataURL();
    },

    async createWatermarkImage() {
      if (!this.spaceData) {
        return;
      }
      
      this.calculateWatermarkDimensions();
      
      const canvas = this.createCanvas();
      if (!canvas) {
        return;
      }
      
      this.setupCanvas(canvas);
      this.drawWatermarkContent(canvas);
      this.finalizeWatermarkImage(canvas);
    },

    createCanvas(): HTMLCanvasElement | null {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      return context ? canvas : null;
    },

    setupCanvas(canvas: HTMLCanvasElement) {
      const context = canvas.getContext('2d')!;
      const scale = 2;
      
      canvas.width = this.watermark.width * scale;
      canvas.height = this.watermark.height * scale;
      context.scale(scale, scale);
      
      context.fillStyle = 'white';
      context.fillRect(0, 0, this.watermark.width, this.watermark.height);
    },

    drawWatermarkContent(canvas: HTMLCanvasElement) {
      const hasLogo = this.spaceData?.logo && this.spaceData.logo.trim();
      const canDrawLogo = hasLogo && this.logoLoaded && this.logoImageElement;
      
      if (canDrawLogo) {
        this.drawWatermarkWithLogo(canvas);
      } else {
        this.createWatermarkImageWithoutLogo(canvas);
      }
    },

    drawWatermarkWithLogo(canvas: HTMLCanvasElement) {
      const context = canvas.getContext('2d')!;
      const logoDimensions = this.calculateLogoDimensions();
      
      this.adjustCanvasWidthForLogo(logoDimensions.width);
      this.drawLogo(context, logoDimensions);
      this.drawTextBelowLogo(context, logoDimensions.height);
    },

    calculateLogoDimensions() {
      const maxLogoHeight = 100;
      const logoAspectRatio = this.logoImageElement!.naturalWidth / this.logoImageElement!.naturalHeight;
      const logoHeight = Math.min(maxLogoHeight, 100);
      const logoWidth = logoHeight * logoAspectRatio;
      
      return { width: logoWidth, height: logoHeight };
    },

    adjustCanvasWidthForLogo(logoWidth: number) {
      const requiredWidth = Math.max(this.watermark.width, logoWidth + 16);
      if (requiredWidth > this.watermark.width) {
        this.watermark.width = requiredWidth;
      }
    },

    drawLogo(context: CanvasRenderingContext2D, logoDimensions: { width: number; height: number }) {
      const logoX = 8;
      const logoY = 8;
      
      context.drawImage(
        this.logoImageElement!,
        logoX,
        logoY,
        logoDimensions.width,
        logoDimensions.height
      );
    },

    drawTextBelowLogo(context: CanvasRenderingContext2D, logoHeight: number) {
      context.fillStyle = '#333';
      context.font = 'bold 12px Arial';
      context.textAlign = 'left';
      context.textBaseline = 'top';
      
      const textX = 8;
      const textY = 8 + logoHeight + 8;
      const lineHeight = 14;
      const lines = this.getSpaceDataLines();
      
      lines.forEach((line, index) => {
        if (line && line.trim()) {
          context.fillText(line, textX, textY + (index * lineHeight));
        }
      });
    },

    getSpaceDataLines(): string[] {
      const lines: string[] = [];
      
      // Add name if available
      if (this.spaceData?.name && this.spaceData.name.trim()) {
        lines.push(this.spaceData.name.toUpperCase());
      }
      
      // Add address if available
      if (this.spaceData?.address && this.spaceData.address.trim()) {
        lines.push(this.spaceData.address);
      }
      
      // Add city and zipcode if available
      const city = this.spaceData?.city && this.spaceData.city.trim() ? this.spaceData.city : '';
      const zipcode = this.spaceData?.zipcode && this.spaceData.zipcode.trim() ? this.spaceData.zipcode : '';
      if (city || zipcode) {
        lines.push(`${zipcode} ${city}`.trim());
      }
      
      // Add phone if available
      if (this.spaceData?.phone && this.spaceData.phone.trim()) {
        lines.push(this.spaceData.phone);
      }
      
      // Add email if available
      if (this.spaceData?.default_notification_email && this.spaceData.default_notification_email.trim()) {
        lines.push(this.spaceData.default_notification_email);
      }
      
      return lines;
    },

    finalizeWatermarkImage(canvas: HTMLCanvasElement) {
      this.watermarkImage = new Image();
      this.watermarkImage.onload = () => {
        this.addWatermarkToHistory();
      };
      this.watermarkImage.src = canvas.toDataURL();
    },

    createWatermarkImageWithoutLogo(canvas: HTMLCanvasElement) {
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      
      // Calculate real dimensions based on content
      this.calculateWatermarkDimensions();
      
      // Calculate required dimensions first
      const lineHeight = 14;
      const lines = this.getSpaceDataLines();
      
      
      // Set high resolution for better text quality
      const scale = 2;
      canvas.width = this.watermark.width * scale;
      canvas.height = this.watermark.height * scale;
      context.scale(scale, scale);
      
      // Draw white background
      context.fillStyle = 'white';
      context.fillRect(0, 0, this.watermark.width, this.watermark.height);
      
      // Draw text content (left-aligned)
      context.fillStyle = '#333';
      context.font = 'bold 12px Arial';
      context.textAlign = 'left';
      context.textBaseline = 'top';
      
      const textX = 8; // Left-aligned
      const textY = 8;
      
      
      lines.forEach((line, index) => {
        if (line && line.trim()) {
          context.fillText(line, textX, textY + (index * lineHeight));
        }
      });
      
      this.watermarkImage = new Image();
      this.watermarkImage.onload = () => {
        this.addWatermarkToHistory();
      };
      this.watermarkImage.src = canvas.toDataURL();
    },

    async addWatermarkToHistory() {
      if (!this.watermarkImage) {
        return;
      }
      
      // Store the actual image data in the history
      this.saveToHistory({
        x: this.watermark.x,
        y: this.watermark.y,
        width: this.watermark.width,
        height: this.watermark.height,
        background: 'watermark',
        'inline-content': null,
        deleted: false,
        pageNum: this.pageNum,
        imageData: this.watermarkImage.src // Store the actual rendered image
      });
      
      // Redraw canvas to show watermark
      await this.redrawZonesOnly();
    },

    startWatermarkInteraction(event: MouseEvent) {
      if (!this.singleCanvas || !this.watermarkImage) return;
      
      const rect = this.singleCanvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      // Check if click is within watermark bounds
      if (mouseX >= this.watermark.x && mouseX <= this.watermark.x + this.watermark.width &&
          mouseY >= this.watermark.y && mouseY <= this.watermark.y + this.watermark.height) {
        
        // Check if click is on a resize handle (corners)
        const handleSize = 8;
        const isOnResizeHandle = this.isOnResizeHandle(mouseX, mouseY, handleSize);
        
        if (isOnResizeHandle) {
          this.watermark.isResizing = true;
          this.watermark.resizeHandle = isOnResizeHandle;
          this.watermark.dragStartX = mouseX;
          this.watermark.dragStartY = mouseY;
          this.watermark.originalWidth = this.watermark.width;
          this.watermark.originalHeight = this.watermark.height;
          this.watermark.originalX = this.watermark.x;
          this.watermark.originalY = this.watermark.y;
        } else {
          this.watermark.isDragging = true;
          this.watermark.dragStartX = mouseX - this.watermark.x;
          this.watermark.dragStartY = mouseY - this.watermark.y;
        }
      }
    },

    isOnResizeHandle(mouseX: number, mouseY: number, handleSize: number): string | null {
      const { x, y, width, height } = this.watermark;
      
      // Check each corner
      if (mouseX >= x - handleSize/2 && mouseX <= x + handleSize/2 &&
          mouseY >= y - handleSize/2 && mouseY <= y + handleSize/2) {
        return 'nw'; // Top-left
      }
      if (mouseX >= x + width - handleSize/2 && mouseX <= x + width + handleSize/2 &&
          mouseY >= y - handleSize/2 && mouseY <= y + handleSize/2) {
        return 'ne'; // Top-right
      }
      if (mouseX >= x - handleSize/2 && mouseX <= x + handleSize/2 &&
          mouseY >= y + height - handleSize/2 && mouseY <= y + height + handleSize/2) {
        return 'sw'; // Bottom-left
      }
      if (mouseX >= x + width - handleSize/2 && mouseX <= x + width + handleSize/2 &&
          mouseY >= y + height - handleSize/2 && mouseY <= y + height + handleSize/2) {
        return 'se'; // Bottom-right
      }
      
      return null;
    },

    async handleWatermarkInteraction(event: MouseEvent) {
      if ((!this.watermark.isDragging && !this.watermark.isResizing) || !this.singleCanvas || !this.watermarkImage) return;
      
      const rect = this.singleCanvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      if (this.watermark.isDragging) {
        // Handle dragging (moving the watermark)
        const newX = mouseX - this.watermark.dragStartX;
        const newY = mouseY - this.watermark.dragStartY;
        
        // Constrain to canvas bounds
        this.watermark.x = Math.max(0, Math.min(newX, rect.width - this.watermark.width));
        this.watermark.y = Math.max(0, Math.min(newY, rect.height - this.watermark.height));
      } else if (this.watermark.isResizing) {
        // Handle resizing with aspect ratio preservation
        const deltaX = mouseX - this.watermark.dragStartX;
        const originalAspectRatio = this.watermarkImage.naturalWidth / this.watermarkImage.naturalHeight;
        
        let newWidth = this.watermark.originalWidth;
        let newHeight = this.watermark.originalHeight;
        let newX = this.watermark.originalX;
        let newY = this.watermark.originalY;
        
        switch (this.watermark.resizeHandle) {
          case 'se': // Bottom-right
            newWidth = Math.max(20, this.watermark.originalWidth + deltaX);
            newHeight = newWidth / originalAspectRatio;
            break;
          case 'sw': // Bottom-left
            newWidth = Math.max(20, this.watermark.originalWidth - deltaX);
            newHeight = newWidth / originalAspectRatio;
            newX = this.watermark.originalX + (this.watermark.originalWidth - newWidth);
            break;
          case 'ne': // Top-right
            newWidth = Math.max(20, this.watermark.originalWidth + deltaX);
            newHeight = newWidth / originalAspectRatio;
            newY = this.watermark.originalY + (this.watermark.originalHeight - newHeight);
            break;
          case 'nw': // Top-left
            newWidth = Math.max(20, this.watermark.originalWidth - deltaX);
            newHeight = newWidth / originalAspectRatio;
            newX = this.watermark.originalX + (this.watermark.originalWidth - newWidth);
            newY = this.watermark.originalY + (this.watermark.originalHeight - newHeight);
            break;
        }
        
        // Constrain to canvas bounds
        this.watermark.x = Math.max(0, Math.min(newX, rect.width - newWidth));
        this.watermark.y = Math.max(0, Math.min(newY, rect.height - newHeight));
        this.watermark.width = Math.min(newWidth, rect.width - this.watermark.x);
        this.watermark.height = Math.min(newHeight, rect.height - this.watermark.y);
      }
      
      // Update history entry
      this.updateLastHistoryEntry({
        x: this.watermark.x,
        y: this.watermark.y,
        width: this.watermark.width,
        height: this.watermark.height
      }, 'watermark');
      
      // Redraw canvas
      await this.redrawZonesOnly();
    },

    stopWatermarkInteraction(event: MouseEvent) {
      this.watermark.isDragging = false;
      this.watermark.isResizing = false;
      this.watermark.resizeHandle = null;
    },

    startWatermarkDrawing(event: MouseEvent) {
      if (!this.singleCanvas) return;
      event.preventDefault();
      
      // Create watermark image if it doesn't exist
      if (!this.watermarkImage) {
        this.createWatermarkImage();
        // Wait a bit for the image to be created
        setTimeout(() => {
          this.startWatermarkDrawingInternal(event);
        }, 100);
        return;
      }
      
      this.startWatermarkDrawingInternal(event);
    },

    startWatermarkDrawingInternal(event: MouseEvent) {
      if (!this.singleCanvas) return;
      this.isDrawing = true;
      const rect = this.singleCanvas.getBoundingClientRect();
      this.startX = event.clientX - rect.left;
      this.startY = event.clientY - rect.top;
      
      // Create initial watermark area
      this.currentWatermarkArea = {
        x: this.startX,
        y: this.startY,
        width: 0,
        height: 0
      };
    },

    async drawWatermark(event: MouseEvent) {
      if (!this.isDrawing || !this.singleCanvas || !this.watermarkImage) return;
      event.preventDefault();
      const rect = this.singleCanvas.getBoundingClientRect();
      const currentX = event.clientX - rect.left;
      const currentY = event.clientY - rect.top;
      
      // Calculate the original aspect ratio of the watermark image
      const originalAspectRatio = this.watermarkImage.naturalWidth / this.watermarkImage.naturalHeight;
      
      // Calculate the raw width and height from mouse movement
      const rawWidth = Math.abs(currentX - this.startX);
      const rawHeight = Math.abs(currentY - this.startY);
      
      // Determine which dimension to use as the primary constraint
      // Use the larger dimension to maintain aspect ratio
      let width, height;
      if (rawWidth / originalAspectRatio > rawHeight) {
        // Width is the constraint
        width = rawWidth;
        height = rawWidth / originalAspectRatio;
      } else {
        // Height is the constraint
        height = rawHeight;
        width = rawHeight * originalAspectRatio;
      }
      
      const area = {
        x: Math.min(this.startX, currentX),
        y: Math.min(this.startY, currentY),
        width: width,
        height: height
      };
      
      this.currentWatermarkArea = area;
      
      // Use cached image data for smooth drawing without flickering
      await this.redrawZonesOnly();
    },

    async stopWatermarkDrawing(event: MouseEvent) {
      if (!this.isDrawing) return;
      event.preventDefault();
      this.isDrawing = false;
      
      if (this.currentWatermarkArea && this.currentWatermarkArea.width > 10 && this.currentWatermarkArea.height > 10) {
        // Add watermark to history with stored image data
        this.saveToHistory({
          x: this.currentWatermarkArea.x,
          y: this.currentWatermarkArea.y,
          width: this.currentWatermarkArea.width,
          height: this.currentWatermarkArea.height,
          background: 'watermark',
          'inline-content': null,
          deleted: false,
          pageNum: this.pageNum,
          imageData: this.watermarkImage ? this.watermarkImage.src : undefined
        });
        this.currentWatermarkArea = null;
      } else {
        // Remove if too small
        this.currentWatermarkArea = null;
      }
      
      // Use cached image data instead of full re-render to prevent flickering
      await this.redrawZonesOnly();
    },

    createTestWatermark() {
      // Create a simple test watermark
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;
      
      canvas.width = this.watermark.width;
      canvas.height = this.watermark.height;
      
      // Draw test background
      context.fillStyle = 'rgba(255, 255, 255, 0.9)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw test text
      context.fillStyle = '#333';
      context.font = 'bold 14px Arial';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText('TEST WATERMARK', canvas.width / 2, canvas.height / 2);
      
      // Create image
      this.watermarkImage = new Image();
      this.watermarkImage.onload = () => {
        this.addWatermarkToHistory();
      };
      this.watermarkImage.src = canvas.toDataURL();
    },

    // Overlay interaction methods
    handleOverlayMouseDown(event: MouseEvent) {
      if (!this.lastNonDeletedEntry || this.isDrawing) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      const target = event.target as HTMLElement;
      const handle = target.getAttribute('data-handle');
      
      this.overlayInteraction.isActive = true;
      this.overlayInteraction.targetEntry = this.lastNonDeletedEntry;
      this.overlayInteraction.dragStartX = event.clientX;
      this.overlayInteraction.dragStartY = event.clientY;
      this.overlayInteraction.originalX = this.lastNonDeletedEntry.x;
      this.overlayInteraction.originalY = this.lastNonDeletedEntry.y;
      this.overlayInteraction.originalWidth = this.lastNonDeletedEntry.width;
      this.overlayInteraction.originalHeight = this.lastNonDeletedEntry.height;
      
      if (handle) {
        // Resizing - add global event listeners
        this.overlayInteraction.isResizing = true;
        this.overlayInteraction.resizeHandle = handle;
        document.addEventListener('mousemove', this.handleGlobalMouseMove);
        document.addEventListener('mouseup', this.handleGlobalMouseUp);
      } else {
        // Dragging - add global event listeners
        this.overlayInteraction.isDragging = true;
        document.addEventListener('mousemove', this.handleGlobalMouseMove);
        document.addEventListener('mouseup', this.handleGlobalMouseUp);
      }
    },

    handleOverlayMouseMove(event: MouseEvent) {
      // This method is kept for backward compatibility but now delegates to global handler
      this.handleGlobalMouseMove(event);
    },

    handleGlobalMouseMove(event: MouseEvent) {
      if (!this.overlayInteraction.isActive || !this.overlayInteraction.targetEntry) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      const deltaX = event.clientX - this.overlayInteraction.dragStartX;
      const deltaY = event.clientY - this.overlayInteraction.dragStartY;
      
      if (this.overlayInteraction.isDragging) {
        // Handle dragging
        const newX = this.overlayInteraction.originalX + deltaX;
        const newY = this.overlayInteraction.originalY + deltaY;
        
        // Constrain to canvas bounds
        if (this.singleCanvas) {
          const rect = this.singleCanvas.getBoundingClientRect();
          const constrainedX = Math.max(0, Math.min(newX, rect.width - this.overlayInteraction.originalWidth));
          const constrainedY = Math.max(0, Math.min(newY, rect.height - this.overlayInteraction.originalHeight));
          
          this.overlayInteraction.targetEntry.x = constrainedX;
          this.overlayInteraction.targetEntry.y = constrainedY;
        }
      } else if (this.overlayInteraction.isResizing) {
        // Handle resizing
        this.handleOverlayResize(deltaX, deltaY);
      }
      
      // Redraw canvas to show changes
      this.redrawZonesOnly();
    },

    handleOverlayResize(deltaX: number, deltaY: number) {
      if (!this.overlayInteraction.targetEntry || !this.overlayInteraction.resizeHandle) return;
      
      const handle = this.overlayInteraction.resizeHandle;
      let newX = this.overlayInteraction.originalX;
      let newY = this.overlayInteraction.originalY;
      let newWidth = this.overlayInteraction.originalWidth;
      let newHeight = this.overlayInteraction.originalHeight;
      
      // Check if this is a watermark entry and get its aspect ratio
      const isWatermark = this.overlayInteraction.targetEntry.background === 'watermark';
      let aspectRatio = 1;
      
      if (isWatermark && this.watermarkImage) {
        aspectRatio = this.watermarkImage.naturalWidth / this.watermarkImage.naturalHeight;
      }
      
      switch (handle) {
        case 'se': // Bottom-right
          newWidth = Math.max(20, this.overlayInteraction.originalWidth + deltaX);
          if (isWatermark) {
            newHeight = newWidth / aspectRatio;
          } else {
            newHeight = Math.max(20, this.overlayInteraction.originalHeight + deltaY);
          }
          break;
        case 'sw': // Bottom-left
          newWidth = Math.max(20, this.overlayInteraction.originalWidth - deltaX);
          if (isWatermark) {
            newHeight = newWidth / aspectRatio;
            newX = this.overlayInteraction.originalX + (this.overlayInteraction.originalWidth - newWidth);
          } else {
            newHeight = Math.max(20, this.overlayInteraction.originalHeight + deltaY);
            newX = this.overlayInteraction.originalX + (this.overlayInteraction.originalWidth - newWidth);
          }
          break;
        case 'ne': // Top-right
          newWidth = Math.max(20, this.overlayInteraction.originalWidth + deltaX);
          if (isWatermark) {
            newHeight = newWidth / aspectRatio;
            newY = this.overlayInteraction.originalY + (this.overlayInteraction.originalHeight - newHeight);
          } else {
            newHeight = Math.max(20, this.overlayInteraction.originalHeight - deltaY);
            newY = this.overlayInteraction.originalY + (this.overlayInteraction.originalHeight - newHeight);
          }
          break;
        case 'nw': // Top-left
          newWidth = Math.max(20, this.overlayInteraction.originalWidth - deltaX);
          if (isWatermark) {
            newHeight = newWidth / aspectRatio;
            newX = this.overlayInteraction.originalX + (this.overlayInteraction.originalWidth - newWidth);
            newY = this.overlayInteraction.originalY + (this.overlayInteraction.originalHeight - newHeight);
          } else {
            newHeight = Math.max(20, this.overlayInteraction.originalHeight - deltaY);
            newX = this.overlayInteraction.originalX + (this.overlayInteraction.originalWidth - newWidth);
            newY = this.overlayInteraction.originalY + (this.overlayInteraction.originalHeight - newHeight);
          }
          break;
      }
      
      // Constrain to canvas bounds
      if (this.singleCanvas) {
        const rect = this.singleCanvas.getBoundingClientRect();
        newX = Math.max(0, Math.min(newX, rect.width - newWidth));
        newY = Math.max(0, Math.min(newY, rect.height - newHeight));
        newWidth = Math.min(newWidth, rect.width - newX);
        newHeight = Math.min(newHeight, rect.height - newY);
      }
      
      this.overlayInteraction.targetEntry.x = newX;
      this.overlayInteraction.targetEntry.y = newY;
      this.overlayInteraction.targetEntry.width = newWidth;
      this.overlayInteraction.targetEntry.height = newHeight;
    },

    handleOverlayMouseUp(event: MouseEvent) {
      // This method is kept for backward compatibility but now delegates to global handler
      this.handleGlobalMouseUp(event);
    },

    handleGlobalMouseUp(event: MouseEvent) {
      if (!this.overlayInteraction.isActive) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      // Remove global event listeners
      document.removeEventListener('mousemove', this.handleGlobalMouseMove);
      document.removeEventListener('mouseup', this.handleGlobalMouseUp);
      
      // Reset interaction state
      this.overlayInteraction.isActive = false;
      this.overlayInteraction.isDragging = false;
      this.overlayInteraction.isResizing = false;
      this.overlayInteraction.resizeHandle = null;
      this.overlayInteraction.targetEntry = null;
    },

    // Helper method to wrap text within box boundaries
    wrapText(context: CanvasRenderingContext2D, text: string, maxWidth: number, maxHeight: number): string[] {
      const lineHeight = 18;
      const maxLines = Math.floor(maxHeight / lineHeight);
      const allLines: string[] = [];
      
      // First, split by user's manual line breaks (\n)
      const userLines = text.split('\n');
      
      for (const userLine of userLines) {
        if (allLines.length >= maxLines) break;
        
        // Handle empty lines (multiple line breaks)
        if (userLine.trim() === '') {
          allLines.push('');
          continue;
        }
        
        const words = userLine.split(' ');
        let currentLine = '';
        
        for (let i = 0; i < words.length; i++) {
          const testLine = currentLine + (currentLine ? ' ' : '') + words[i];
          const metrics = context.measureText(testLine);
          
          if (metrics.width > maxWidth && currentLine) {
            allLines.push(currentLine);
            currentLine = words[i];
            
            // Check if we've exceeded the maximum number of lines
            if (allLines.length >= maxLines) {
              // Add ellipsis to the last line if there are more words
              if (i < words.length - 1) {
                const lastLine = allLines[allLines.length - 1];
                const ellipsis = '...';
                let truncatedLine = lastLine;
                
                // Remove words until we can fit the ellipsis
                while (context.measureText(truncatedLine + ellipsis).width > maxWidth && truncatedLine.length > 0) {
                  truncatedLine = truncatedLine.slice(0, -1);
                }
                
                allLines[allLines.length - 1] = truncatedLine + ellipsis;
              }
              return allLines;
            }
          } else {
            currentLine = testLine;
          }
        }
        
        // Add the current line (even if empty, to preserve spacing)
        if (allLines.length < maxLines) {
          allLines.push(currentLine);
        }
      }
      
      return allLines;
    },

    // Helper method to draw wrapped text
    drawWrappedText(context: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, maxHeight: number) {
      if (!text) return;
      
      // Set text properties
      context.fillStyle = '#333';
      context.font = 'bold 14px Arial';
      context.textAlign = 'left';
      context.textBaseline = 'top';
      
      // Calculate available space (subtract padding)
      const padding = 8;
      const availableWidth = maxWidth - (padding * 2);
      const availableHeight = maxHeight - (padding * 2);
      
      // Wrap the text
      const lines = this.wrapText(context, text, availableWidth, availableHeight);
      
      // Draw each line
      const lineHeight = 18;
      lines.forEach((line, index) => {
        context.fillText(line, x + padding, y + padding + (index * lineHeight));
      });
    }
  }
});
